import { Anchor, Button, Divider, IconFont, Input, Modal, Toast } from '@byecode/ui'
import { Radio, RadioGroup } from '@byecode/ui/components/Radio'
import { CopyButton, ErrorMessage, SERVICES_IP, TagIcon, useAtomAction, useAtomData } from '@lighthouse/shared'
import copyToClipboard from 'copy-to-clipboard'
import { find } from 'rambda'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useAsyncFn } from 'react-use'

import { fetchOtherPlatformsAtom } from '@/atoms/application/action'
import { otherPlatformListAtom } from '@/atoms/application/state'
import { otherPlatformBackgroundColorMap, otherPlatformColorMap, otherPlatformNameMap } from '@/containers/OtherPlatforms/constant'
import { usePageURI } from '@/hooks/usePageURI'
import * as srv from '@/services'

import { PLACEHOLDER_PASSWORD } from '../constants'
import * as SC from '../styles'
import type { WeChatOfficialAccountFormState } from './type'

interface WeChatOfficialAccountProps {
    isPerfectWebsiteInfo?: boolean
    onClose?: () => void
}

const initData: WeChatOfficialAccountFormState = {
    appId: '',
    appSecret: '',
    domainFileName: '',
    domainFileContent: '',
    scope: 'snsapi_userinfo',
    encodingAesKey: '',
    token: ''
}
const WeChatOfficialAccount: React.FunctionComponent<WeChatOfficialAccountProps> = ({ isPerfectWebsiteInfo, onClose }) => {
    const otherPlatforms = useAtomData(otherPlatformListAtom)
    const { run: fetchOtherPlatforms } = useAtomAction(fetchOtherPlatformsAtom)
    const websiteRef = useRef<HTMLInputElement>(null)
    const serviceRef = useRef<HTMLDivElement>(null)
    const { shareUrl, host } = usePageURI({ isProdHost: true })

    const serviceUrl = `https://${host}/byecode/external/wechat/official/callback`

    const [copyLoading, setCopyLoading] = useState(false)
    const [{ loading }, doUpdateIntegration] = useAsyncFn(srv.updateIntegration)

    const account = useMemo(() => {
        const otherPlatform = find(item => item.type === 'WECHAT_OFFICIAL_ACCOUNT', otherPlatforms ?? [])
        return otherPlatform?.configurationComplete && otherPlatform.type === 'WECHAT_OFFICIAL_ACCOUNT'
            ? {
                  id: otherPlatform?.id,
                  ...otherPlatform?.config,
                  appSecret: PLACEHOLDER_PASSWORD,
                  token: otherPlatform?.config.token && PLACEHOLDER_PASSWORD,
                  encodingAesKey: otherPlatform?.config.encodingAesKey && PLACEHOLDER_PASSWORD
              }
            : { ...initData, id: otherPlatform?.id }
    }, [otherPlatforms])

    const {
        register,
        handleSubmit,
        getValues,
        control,
        formState: { errors }
    } = useForm({
        mode: 'onSubmit',
        defaultValues: account
    })

    const iconEle = useMemo(() => {
        if (copyLoading) {
            return <IconFont size={16} color="var(--color-green-500)" type="Tick" />
        }
        return <IconFont color="var(--color-gray-400)" type="Copy" size={16} />
    }, [copyLoading])

    useEffect(() => {
        const timer = setTimeout(() => {
            if (copyLoading) {
                setCopyLoading(false)
            }
        }, 3000)

        return () => {
            clearTimeout(timer)
        }
    }, [copyLoading])

    useEffect(() => {
        if (websiteRef.current && serviceRef.current && isPerfectWebsiteInfo) {
            serviceRef.current?.scrollTo({ top: serviceRef.current.clientHeight })
            websiteRef.current.focus()
        }
    }, [isPerfectWebsiteInfo])

    const handleUpdate = useCallback(
        async (params: WeChatOfficialAccountFormState) => {
            const { id, appId, appSecret, domainFileName, domainFileContent, scope, encodingAesKey, token } = params
            if (!id || loading) {
                return
            }
            const res = await doUpdateIntegration({
                type: 'WECHAT_OFFICIAL_ACCOUNT',
                id,
                config: {
                    appId,
                    appSecret: appSecret === PLACEHOLDER_PASSWORD ? undefined : appSecret,
                    domainFileName,
                    domainFileContent,
                    scope,
                    encodingAesKey: encodingAesKey === PLACEHOLDER_PASSWORD ? undefined : encodingAesKey,
                    token: token === PLACEHOLDER_PASSWORD ? undefined : token
                }
            })
            if (res) {
                fetchOtherPlatforms()
                onClose?.()
            }
        },
        [doUpdateIntegration, fetchOtherPlatforms, loading, onClose]
    )

    const handleInviteSubmit = useCallback(
        (ev: React.FormEvent) => {
            ev.preventDefault()
            handleSubmit(handleUpdate)()
        },
        [handleSubmit, handleUpdate]
    )

    return (
        <SC.Container onSubmit={handleInviteSubmit} autoComplete="off">
            <SC.Content ref={serviceRef}>
                <div style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 11, paddingTop: 16 }}>
                    <TagIcon
                        size={48}
                        iconSize={32}
                        icon="WeChatLogo"
                        radius={6}
                        iconColor={otherPlatformColorMap['WECHAT_PAY']}
                        background={otherPlatformBackgroundColorMap['WECHAT_PAY']}
                    />
                    <SC.Title>{otherPlatformNameMap['WECHAT_OFFICIAL_ACCOUNT']}</SC.Title>
                    <SC.Tip>
                        <SC.Icon color="var(--color-main)" type="WarningCircle" />
                        <SC.Text>如果您完善授权时遇到问题，请查看帮助文档：</SC.Text>
                        <Anchor
                            target="_blank"
                            style={{ fontWeight: 600, color: 'var(--color-main)' }}
                            to="https://byecodehelp.yuque.com/org-wiki-byecodehelp-zavfcl/oevich/okubts70erqfv6z7"
                        >
                            微信公众号授权配置
                        </Anchor>
                    </SC.Tip>
                </div>
                <SC.Item>
                    <SC.Text>
                        开发者ID(AppID)
                        <SC.Text color="red" style={{ paddingLeft: 4 }}>
                            *
                        </SC.Text>
                    </SC.Text>
                    <ErrorMessage name="appId" errors={errors}>
                        <Input placeholder="请填入微信公众号的开发者AppId" {...register('appId', { required: '不能为空' })} />
                    </ErrorMessage>
                    <SC.Description>
                        需先登录「{' '}
                        <Anchor target="_blank" to="https://mp.weixin.qq.com/">
                            微信公众号
                        </Anchor>
                        」&gt;「设置与开发」&gt;「开发接口管理」&gt;「基本配置」&gt;「账号开发信息」找到对应的AppID
                    </SC.Description>
                </SC.Item>
                <SC.Item>
                    <SC.Text>
                        开发者密码(AppSecret)
                        <SC.Text color="red" style={{ paddingLeft: 4 }}>
                            *
                        </SC.Text>
                    </SC.Text>
                    <ErrorMessage name="appSecret" errors={errors}>
                        <Controller
                            name="appSecret"
                            control={control}
                            rules={{ required: '不能为空' }}
                            render={({ field }) => {
                                return (
                                    <Input
                                        autoComplete="new-password"
                                        type="password"
                                        placeholder="请填入微信公众号的开发者密码"
                                        onFocus={() => {
                                            if (field.value === PLACEHOLDER_PASSWORD) {
                                                field.onChange('')
                                            }
                                        }}
                                        onBlur={() => {
                                            if (!field.value.trim()) {
                                                field.onChange(PLACEHOLDER_PASSWORD)
                                            }
                                        }}
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                )
                            }}
                        />
                    </ErrorMessage>
                    <SC.Description>
                        需先登录「
                        <Anchor target="_blank" to="https://mp.weixin.qq.com/">
                            微信公众号
                        </Anchor>
                        」&gt;「设置与开发」 &gt;「开发接口管理」&gt;「基本配置」&gt;「账号开发信息」找到对应的AppSecret
                    </SC.Description>
                </SC.Item>
                <SC.Item>
                    <SC.Text>
                        域名校验文件名
                        <SC.Text color="red" style={{ paddingLeft: 4 }}>
                            *
                        </SC.Text>
                    </SC.Text>
                    <ErrorMessage name="domainFileName" errors={errors}>
                        <Input
                            placeholder="请输入微信公众号提供的域名校验文件名"
                            {...register('domainFileName', { required: '不能为空' })}
                        />
                    </ErrorMessage>
                    <SC.Description>
                        需先登录「{' '}
                        <Anchor target="_blank" to="https://mp.weixin.qq.com/">
                            微信公众号
                        </Anchor>
                        」&gt;「设置与开发」&gt;「账号设置」&gt;「功能设置」&gt;
                        「服务器配置」&gt;「JS接口安全域名」，点击“设置”按钮，找到域名校验文件，复制文件名（文件名的格式类似：MP_verify_t1op33AC5w4rNIwE.txt），然后复制文件名，粘贴到这里
                    </SC.Description>
                </SC.Item>
                <SC.Item>
                    <SC.Text>
                        域名校验文件内容
                        <SC.Text color="red" style={{ paddingLeft: 4 }}>
                            *
                        </SC.Text>
                    </SC.Text>
                    <ErrorMessage name="domainFileContent" errors={errors}>
                        <Input
                            placeholder="请输入微信公众号提供的域名校验文件内容"
                            {...register('domainFileContent', { required: '不能为空' })}
                        />
                    </ErrorMessage>
                    <SC.Description>
                        需先登录「
                        <Anchor target="_blank" to="https://mp.weixin.qq.com/">
                            微信公众号
                        </Anchor>
                        」&gt;「设置与开发」&gt;「账号设置」&gt;「功能设置」&gt;
                        「服务器配置」&gt;「JS接口安全域名」，点击“设置”按钮，找到域名校验文件，点击下载，然后打开文件，将文件中的内容粘贴到这里，文件内容的格式类似：E10p22BD7w1rMitt
                    </SC.Description>
                </SC.Item>
                <SC.Item>
                    <SC.Text>Callback URL</SC.Text>
                    <Input readOnly defaultValue={shareUrl} disabled placeholder="请输入你的业务回调链接" />
                </SC.Item>
                <SC.Item>
                    <SC.Text>Scopes</SC.Text>
                    <Controller
                        control={control}
                        name="scope"
                        render={({ field }) => (
                            <RadioGroup style={{ gap: 24 }} name="scope" value={field.value} onChange={field.onChange}>
                                <Radio size="xs" label="snsapi_userinfo" value="snsapi_userinfo" />
                                <Radio size="xs" label="snsapi_base" value="snsapi_base" />
                            </RadioGroup>
                        )}
                    />
                    <SC.Description>
                        应用授权作用域，snsapi_base（不弹出授权页面，直接跳转，只能获取用户openid），snsapi_userinfo（弹出授权页面，可通过openid拿到呢称、性别、所在地。并且，即使在未关注的情况下，只要用户授权，也能获取其信息）。详情请见
                        <Anchor
                            target="_blank"
                            style={{ color: 'var(--color-main)', paddingLeft: 4 }}
                            to="https://developers.weixin.qq.com/doc/offiaccount/OA_Web_Apps/Wechat_webpage_authorization.html  "
                        >
                            微信公众号网页授权文档说明
                        </Anchor>
                        。
                    </SC.Description>
                </SC.Item>
                <Divider
                    style={{ marginTop: 20, lineHeight: '40px' }}
                    styles={{
                        label: {
                            fontSize: '14px!important',
                            color: 'var(--color-gray-400)!important'
                        }
                    }}
                    labelPosition="center"
                    label="若想开启“微信网页扫码登录”功能，需完善下方配置"
                    color="var(--color-gray-200)"
                />
                <SC.Item style={{ marginTop: 8 }}>
                    <SC.Text>令牌(Token)</SC.Text>
                    <ErrorMessage name="token" errors={errors}>
                        <Controller
                            name="token"
                            control={control}
                            render={({ field }) => {
                                return (
                                    <Input
                                        autoComplete="new-password"
                                        ref={websiteRef}
                                        type="password"
                                        placeholder="请输入微信公众号提供的 Token"
                                        // onFocus={() => {
                                        //     if (field.value === PLACEHOLDER_PASSWORD) {
                                        //         field.onChange('')
                                        //     }
                                        // }}
                                        // onBlur={() => {
                                        //     if (!field.value?.trim()) {
                                        //         field.onChange(PLACEHOLDER_PASSWORD)
                                        //     }
                                        // }}
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                )
                            }}
                        />
                    </ErrorMessage>
                    <SC.Description>
                        需先登录「
                        <Anchor target="_blank" to="https://mp.weixin.qq.com/">
                            微信公众号
                        </Anchor>
                        」{'>'}「设置与开发」{'>'} 「开发接口管理」{'>'} 「基本配置」{'>'}
                        「服务器配置」{'>'} 找到对应的令牌(Token)
                    </SC.Description>
                </SC.Item>
                <SC.Item>
                    <SC.Text>消息加解密密钥(EncodingAESKey)</SC.Text>
                    <ErrorMessage name="encodingAesKey" errors={errors}>
                        <Controller
                            name="encodingAesKey"
                            control={control}
                            render={({ field }) => {
                                return (
                                    <Input
                                        autoComplete="new-password"
                                        type="password"
                                        placeholder="请输入微信公众号提供的 EncodingAESKey"
                                        onFocus={() => {
                                            if (field.value === PLACEHOLDER_PASSWORD) {
                                                field.onChange('')
                                            }
                                        }}
                                        onBlur={() => {
                                            if (!field.value?.trim()) {
                                                field.onChange(PLACEHOLDER_PASSWORD)
                                            }
                                        }}
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                )
                            }}
                        />
                    </ErrorMessage>
                    <SC.Description>
                        需先登录「{' '}
                        <Anchor target="_blank" to="https://mp.weixin.qq.com/">
                            微信公众号
                        </Anchor>
                        」{'>'}「设置与开发」{'>'} 「开发接口管理」{'>'} 「基本配置」{'>'}
                        「服务器配置」{'>'} 找到对应的消息加解密密钥(EncodingAESKey)
                    </SC.Description>
                </SC.Item>
                <SC.Item>
                    <SC.Text>服务器地址(URL)</SC.Text>

                    <Input
                        styles={{
                            suffix: {
                                cursor: 'pointer'
                            }
                        }}
                        disabled
                        defaultValue={serviceUrl}
                        suffix={<CopyButton value={serviceUrl} />}
                    />

                    <SC.Description>
                        请复制该链接「
                        <Anchor target="_blank" to="https://mp.weixin.qq.com/">
                            微信公众号
                        </Anchor>
                        」{'>'}「设置与开发」{'>'} 「开发接口管理」{'>'} 「基本配置」{'>'}「服务器配置」{'>'}
                        「修改配置」，粘贴到「URL」输入框，点击「提交」按钮
                    </SC.Description>
                </SC.Item>
            </SC.Content>
            <SC.Footer>
                <SC.LeftFill>
                    <Button
                        size="lg"
                        style={{ width: 104 }}
                        onClick={() => {
                            if (copyLoading) {
                                return
                            }
                            setCopyLoading(true)
                            const isCopied = copyToClipboard(SERVICES_IP)
                            if (isCopied) {
                                Toast.success('复制成功')
                            }
                        }}
                        icon={iconEle}
                    >
                        复制IP
                    </Button>
                </SC.LeftFill>
                <SC.RightFill>
                    <Button size="lg" style={{ width: 104 }} onClick={() => onClose?.()}>
                        取消
                    </Button>
                    <Button size="lg" style={{ width: 104 }} loading={loading} type="primary" htmlType="submit">
                        确定
                    </Button>
                </SC.RightFill>
            </SC.Footer>
        </SC.Container>
    )
}

export default WeChatOfficialAccount
